import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { IoClose } from "react-icons/io5";
import { FloorplannerStoreContext } from "../../store/floorplannerStore";
import { editorStoreContext } from "../../store/editorStore";
import { FillPattern } from "../../types/floorplannerTypes";
import { RoomShapeGrid, RoomShapeSilhouette } from "./RoomShapeGrid";
import { RoomDimensionEditor } from "./RoomDimensionEditor";
import { RoomFloorPatternPicker } from "./RoomFloorPatternPicker";
import {
  DEFAULT_ROOM_FLOOR_COLOR_ID,
  RoomFloorColorPicker,
} from "./RoomFloorColorPicker";
import {
  DEFAULT_ROOM_WALL_COLOR_ID,
  RoomWallColorPicker,
} from "./RoomWallColorPicker";
import { RoomFloorColorId, getRoomFloorColor } from "./roomFloorColors";
import { DEFAULT_ROOM_FLOOR_PATTERN } from "./roomFloorPatterns";
import { RoomWallColorId, getRoomWallColor } from "./roomWallColors";
import {
  getRoomShapeDefinition,
  RoomShapeId,
  RoomShapeParams,
  ROOM_SHAPE_IDS,
} from "./roomShapeTemplates";
import { seedRoomFromTemplate } from "./seedRoomFromTemplate";
import { clearRoomPickerFlag } from "./roomPickerSession";

interface RoomPickerWizardProps {
  onComplete: () => void;
  onCancel: () => void;
}

type WizardStep = 1 | 2;

/**
 * Two-step modal: pick a room shape, then edit dimensions, floor, and wall styling.
 * Styled like BackdropImageModal (centered overlay, not full-page).
 */
export const RoomPickerWizard: React.FC<RoomPickerWizardProps> = observer(
  ({ onComplete, onCancel }) => {
    const { t } = useTranslation();
    const floorplannerStore = useContext(FloorplannerStoreContext);
    const editorStore = useContext(editorStoreContext);

    const [step, setStep] = useState<WizardStep>(1);
    const [shapeId, setShapeId] = useState<RoomShapeId>("rectangle");
    const [params, setParams] = useState<RoomShapeParams>(
      () => getRoomShapeDefinition("rectangle").defaultParams,
    );
    const [floorPattern, setFloorPattern] = useState<FillPattern | undefined>(
      DEFAULT_ROOM_FLOOR_PATTERN,
    );
    const [floorColorId, setFloorColorId] = useState<RoomFloorColorId>(
      DEFAULT_ROOM_FLOOR_COLOR_ID,
    );
    const [wallColorId, setWallColorId] = useState<RoomWallColorId>(
      DEFAULT_ROOM_WALL_COLOR_ID,
    );
    const [isFinishing, setIsFinishing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const shapeLabels = useMemo(() => {
      const labels = {} as Record<RoomShapeId, string>;
      for (const id of ROOM_SHAPE_IDS) {
        labels[id] = t(`roomPicker.shapes.${id}`);
      }
      return labels;
    }, [t]);

    const handleSelectShape = (id: RoomShapeId) => {
      setShapeId(id);
      setParams({ ...getRoomShapeDefinition(id).defaultParams });
    };

    const handleFinish = async () => {
      if (isFinishing) return;
      setIsFinishing(true);
      setError(null);
      try {
        const result = await seedRoomFromTemplate({
          floorplannerStore,
          editorStore,
          shapeId,
          params,
          floorPattern,
          floorColorId,
          wallColorId,
        });
        // Shape-cap paywall already shown; keep the wizard open so the user
        // can cancel or upgrade without a half-created room.
        if (result.blocked) {
          setIsFinishing(false);
          return;
        }
        clearRoomPickerFlag();
        onComplete();
      } catch (e) {
        console.error("Failed to seed room from template", e);
        setError(t("roomPicker.finishError"));
        setIsFinishing(false);
      }
    };

    const handleCancel = () => {
      clearRoomPickerFlag();
      onCancel();
    };

    return (
      <div
        className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
        role="presentation"
        onClick={(event) => {
          if (event.target === event.currentTarget && !isFinishing) {
            handleCancel();
          }
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="room-picker-title"
          className="flex max-h-[90vh] w-full max-w-[960px] flex-col overflow-hidden rounded-lg bg-[#f7f6f0] shadow-xl"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
            <div>
              <p className="mb-0.5 text-xs uppercase tracking-wide text-gray-500">
                {t("roomPicker.stepOf", { current: step, total: 2 })}
              </p>
              <h2
                id="room-picker-title"
                className="font-['DM Sans'] text-xl text-gray-900"
              >
                {step === 1
                  ? t("roomPicker.step1Title")
                  : t("roomPicker.step2Title")}
              </h2>
            </div>
            <button
              type="button"
              className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
              onClick={handleCancel}
              disabled={isFinishing}
              aria-label={t("common.close")}
            >
              <IoClose size={24} />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden p-5 md:flex-row">
            <div className="flex w-full shrink-0 flex-col md:w-[300px]">
              <p className="mb-4 text-sm text-gray-600">
                {step === 1
                  ? t("roomPicker.step1Description")
                  : t("roomPicker.step2Description")}
              </p>

              <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                {step === 1 ? (
                  <RoomShapeGrid
                    selectedId={shapeId}
                    onSelect={handleSelectShape}
                    labels={shapeLabels}
                  />
                ) : (
                  <div className="space-y-5 text-sm text-gray-700">
                    <p>{t("roomPicker.clickWallHint")}</p>
                    <p className="rounded-md bg-yellow-50 px-3 py-2 text-yellow-900">
                      {t("roomPicker.unitNote")}
                    </p>
                    <RoomFloorPatternPicker
                      value={floorPattern}
                      onChange={setFloorPattern}
                    />
                    <RoomFloorColorPicker
                      value={floorColorId}
                      onChange={setFloorColorId}
                    />
                    <RoomWallColorPicker
                      value={wallColorId}
                      onChange={setWallColorId}
                    />
                  </div>
                )}
              </div>

              {error && (
                <p className="mt-3 text-sm text-red-600" role="alert">
                  {error}
                </p>
              )}
            </div>

            <div className="relative min-h-[280px] min-w-0 flex-1 overflow-hidden rounded-lg border border-[#dedbd2] bg-white md:min-h-[420px]">
              {step === 1 ? (
                <div className="flex h-full items-center justify-center bg-[#ECECEC] p-8">
                  <RoomShapeSilhouette
                    shapeId={shapeId}
                    params={params}
                    paddingRatio={0.28}
                    className="h-[55%] w-[55%] max-h-[280px] max-w-[280px]"
                  />
                </div>
              ) : (
                <RoomDimensionEditor
                  shapeId={shapeId}
                  params={params}
                  onChangeParams={setParams}
                  floorColorHex={getRoomFloorColor(floorColorId).hex}
                  floorPattern={floorPattern}
                  wallColorHex={getRoomWallColor(wallColorId).hex}
                />
              )}
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-gray-200 px-5 py-4">
            {step === 1 ? (
              <>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="rounded bg-white px-4 py-2 text-sm text-black hover:bg-gray-50"
                >
                  {t("common.back")}
                </button>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="rounded bg-black px-4 py-2 text-sm text-white hover:bg-gray-800"
                >
                  {t("common.next")}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  disabled={isFinishing}
                  className="rounded bg-white px-4 py-2 text-sm text-black hover:bg-gray-50 disabled:opacity-50"
                >
                  {t("common.back")}
                </button>
                <button
                  type="button"
                  onClick={handleFinish}
                  disabled={isFinishing}
                  className="rounded bg-black px-4 py-2 text-sm text-white hover:bg-gray-800 disabled:opacity-50"
                >
                  {isFinishing
                    ? t("common.loadingEllipsis")
                    : t("roomPicker.finish")}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  },
);

export default RoomPickerWizard;
